import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useState } from "react";
import axios from "axios";
import { 
  ArrowRight, 
  CheckCircle2, 
  Zap, 
  Shield, 
  Users, 
  TrendingDown,
  Home,
  Euro,
  Leaf,
  Award,
  ChevronRight,
  Send,
  Headphones,
  Clock,
  MessageCircle,
  Phone,
  Star,
  Sparkles,
  BadgeCheck,
  FileText,
  TrendingUp,
  RotateCcw,
  ClipboardList,
  HandCoins,
  FolderOpen,
  BadgeEuro,
  CheckCheck
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;

const HERO_IMAGE = "https://images.unsplash.com/photo-1687865014576-9ae3570f550a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBlY28lMjBob3VzZSUyMHNvbGFyJTIwcGFuZWxzJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc2NzA1NzU0N3ww&ixlib=rb-4.1.0&q=85";
const FAMILY_IMAGE = "https://images.unsplash.com/photo-1585945148306-db646373834d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxoYXBweSUyMGZhbWlseSUyMG1vZGVybiUyMGJyaWdodCUyMGxpdmluZyUyMHJvb218ZW58MHx8fHwxNzY3MDU3NTUxfDA&ixlib=rb-4.1.0&q=85";

const benefits = [
  {
    icon: Shield,
    title: "Accompagnement Expert",
    description: "Une équipe de professionnels certifiés vous guide à chaque étape de votre projet."
  },
  {
    icon: Euro,
    title: "Optimisation des Aides",
    description: "Maximisez vos économies grâce aux aides d'État et subventions disponibles."
  },
  {
    icon: Zap,
    title: "Simulation Précise",
    description: "Estimations détaillées et personnalisées pour votre logement."
  },
  {
    icon: TrendingDown,
    title: "Réduction des Coûts",
    description: "Diminuez jusqu'à 60% votre facture énergétique annuelle."
  }
];

const stats = [
  { value: "10 000+", label: "Projets accompagnés" },
  { value: "45%", label: "Économies moyennes" },
  { value: "98%", label: "Clients satisfaits" },
  { value: "15M€", label: "D'aides obtenues" }
];

const steps = [
  { icon: Home, title: "Décrivez votre logement", description: "Type de bien, surface, année de construction" },
  { icon: Zap, title: "Analysez votre consommation", description: "Chauffage, isolation, équipements" },
  { icon: Award, title: "Recevez votre diagnostic", description: "Recommandations personnalisées" }
];

export default function HomePage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email });
      toast.success("Merci pour votre inscription !");
      setEmail("");
    } catch {
      toast.error("Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center">
        {/* Background */}
        <div className="absolute inset-0 z-0">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover"
            src="https://customer-assets.emergentagent.com/job_494a1d3d-3b3f-4afe-990a-a7037372e3a6/artifacts/xushhsrx_grok-video-caa3f8cc-737e-4596-bf28-f5e0e3f993bd.mp4"
          />
          {/* Léger dégradé sombre en bas uniquement pour la lisibilité du texte */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-transparent z-10" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-white"
            >
              <span className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 text-sm font-manrope mb-6">
                <Leaf size={16} className="text-primary-neon" />
                Rénovation énergétique simplifiée
              </span>
              
              <h1 className="font-outfit font-bold text-4xl sm:text-5xl lg:text-6xl leading-tight mb-6">
                Transformez votre habitat,
                <span className="text-primary-neon"> économisez</span> sur vos factures
              </h1>
              
              <p className="font-manrope text-lg text-white/80 mb-8 max-w-lg">
                Bénéficiez d'un accompagnement clé en main pour votre projet de rénovation. 
                Simulations précises, aides d'État maximisées et suivi expert.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/simulation">
                  <Button 
                    data-testid="hero-cta-btn"
                    className="btn-primary text-base flex items-center gap-2 w-full sm:w-auto"
                  >
                    Commencer ma simulation gratuite
                    <ArrowRight size={18} />
                  </Button>
                </Link>
                <Link to="/tarifs">
                  <Button 
                    variant="outline"
                    data-testid="hero-pricing-btn"
                    className="bg-white/10 backdrop-blur-sm border-white/30 text-white hover:bg-white/20 rounded-full px-8 py-4 font-outfit font-bold w-full sm:w-auto"
                  >
                    Voir nos offres
                  </Button>
                </Link>
              </div>

              {/* Trust badges */}
              <div className="flex items-center gap-6 mt-10 pt-10 border-t border-white/20">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="text-primary-neon" size={20} />
                  <span className="text-sm text-white/80">100% Gratuit</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="text-primary-neon" size={20} />
                  <span className="text-sm text-white/80">Sans engagement</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="text-primary-neon" size={20} />
                  <span className="text-sm text-white/80">Experts certifiés</span>
                </div>
              </div>
            </motion.div>

            {/* Floating Card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="hidden lg:block"
            >
              <div className="glass-card bg-white/90 relative">
                <div className="absolute -top-4 -right-4 bg-primary text-white px-4 py-2 rounded-full text-sm font-bold">
                  Estimation rapide
                </div>
                <h3 className="font-outfit font-bold text-xl text-text-primary mb-6">
                  Calculez vos économies potentielles
                </h3>
                <div className="space-y-4 mb-6">
                  {steps.map((step, index) => (
                    <div key={index} className="flex items-start gap-4 p-4 bg-secondary/50 rounded-2xl">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <step.icon className="text-primary" size={20} />
                      </div>
                      <div>
                        <h4 className="font-outfit font-semibold text-text-primary">{step.title}</h4>
                        <p className="text-sm text-text-muted font-manrope">{step.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <Link to="/simulation">
                  <Button 
                    data-testid="card-cta-btn"
                    className="btn-primary w-full flex items-center justify-center gap-2"
                  >
                    Lancer ma simulation
                    <ChevronRight size={18} />
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-secondary">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="font-syne text-4xl md:text-5xl font-bold text-primary mb-2">
                  {stat.value}
                </div>
                <div className="font-manrope text-text-secondary text-sm">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <span className="inline-flex items-center gap-2 bg-secondary text-primary rounded-full px-4 py-2 text-sm font-manrope font-semibold mb-4">
              <Leaf size={16} />
              Nos avantages
            </span>
            <h2 className="font-outfit font-bold text-3xl md:text-4xl text-text-primary mb-4">
              Pourquoi choisir Eco-Conseil-Habitat ?
            </h2>
            <p className="font-manrope text-text-secondary">
              Un accompagnement complet pour réussir votre transition énergétique 
              et valoriser votre bien immobilier.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="solid-card group"
                data-testid={`benefit-card-${index}`}
              >
                <div className="w-14 h-14 rounded-2xl bg-secondary flex items-center justify-center mb-6 group-hover:bg-primary group-hover:text-white transition-colors duration-300">
                  <benefit.icon size={28} className="text-primary group-hover:text-white" />
                </div>
                <h3 className="font-outfit font-bold text-lg text-text-primary mb-3">
                  {benefit.title}
                </h3>
                <p className="font-manrope text-text-muted text-sm leading-relaxed">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24 md:py-32">
        <div className="absolute inset-0 bg-forest-dark">
          <img 
            src={FAMILY_IMAGE}
            alt="Famille heureuse"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-outfit font-bold text-3xl md:text-4xl text-white mb-6">
                Prêt à réduire vos factures et améliorer votre confort ?
              </h2>
              <p className="font-manrope text-white/80 text-lg mb-8">
                Rejoignez les milliers de familles qui ont déjà fait confiance à Eco-Conseil-Habitat 
                pour leur projet de rénovation énergétique.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/simulation">
                  <Button 
                    data-testid="cta-section-btn"
                    className="btn-primary flex items-center gap-2"
                  >
                    Commencer maintenant
                    <ArrowRight size={18} />
                  </Button>
                </Link>
                <Link to="/a-propos">
                  <Button 
                    variant="outline"
                    className="bg-transparent border-white/30 text-white hover:bg-white/10 rounded-full px-8 py-4 font-outfit font-bold"
                  >
                    En savoir plus
                  </Button>
                </Link>
              </div>
            </motion.div>

            {/* Newsletter Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="glass-card bg-white/95"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Send className="text-primary" size={24} />
                </div>
                <div>
                  <h3 className="font-outfit font-bold text-lg text-text-primary">Newsletter</h3>
                  <p className="text-sm text-text-muted font-manrope">Actualités et conseils</p>
                </div>
              </div>
              <p className="font-manrope text-text-secondary text-sm mb-6">
                Recevez nos dernières actualités sur les aides d'État, conseils de rénovation 
                et tendances énergie verte.
              </p>
              <form onSubmit={handleNewsletterSubmit} className="space-y-4">
                <Input
                  type="email"
                  placeholder="Votre adresse email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-field"
                  data-testid="newsletter-email-input"
                  required
                />
                <Button 
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full"
                  data-testid="newsletter-submit-btn"
                >
                  {loading ? "Inscription..." : "S'inscrire à la newsletter"}
                </Button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Experts Section */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <span className="inline-flex items-center gap-2 bg-secondary text-primary rounded-full px-4 py-2 text-sm font-manrope font-semibold mb-4">
              <Users size={16} />
              Notre équipe
            </span>
            <h2 className="font-outfit font-bold text-3xl md:text-4xl text-text-primary mb-4">
              Des experts à votre service
            </h2>
            <p className="font-manrope text-text-secondary">
              Une équipe pluridisciplinaire pour vous accompagner dans tous les aspects 
              de votre projet de rénovation.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { title: "Diagnostiqueurs immobiliers", desc: "Experts en performance énergétique et isolation thermique", icon: "🏠" },
              { title: "Conseillers", desc: "Spécialistes des aides d'État et financements", icon: "💼" },
              { title: "Architectes", desc: "Designers d'intérieur et solutions sur-mesure", icon: "✏️" }
            ].map((expert, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative overflow-hidden solid-card group"
              >
                <div className="absolute top-4 right-4 text-4xl opacity-20 group-hover:opacity-30 transition-opacity">
                  {expert.icon}
                </div>
                <h3 className="font-outfit font-bold text-xl text-text-primary mb-3">
                  {expert.title}
                </h3>
                <p className="font-manrope text-text-muted text-sm">
                  {expert.desc}
                </p>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/a-propos">
              <Button 
                variant="outline"
                className="btn-secondary"
                data-testid="experts-link-btn"
              >
                Découvrir notre équipe
                <ChevronRight size={18} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Support & Accompagnement 7j/7 Section */}
      <section className="relative py-24 overflow-hidden">
        {/* Background avec gradient vert écologique */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#064E3B] via-[#065F46] to-[#047857]">
          <div className="absolute inset-0 opacity-30">
            <div className="absolute top-20 left-10 w-72 h-72 bg-emerald-500 rounded-full filter blur-[120px]" />
            <div className="absolute bottom-20 right-10 w-96 h-96 bg-teal-400 rounded-full filter blur-[150px]" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-green-500 rounded-full filter blur-[200px] opacity-20" />
          </div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <motion.div
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ type: "spring", delay: 0.2 }}
              className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-xl border border-white/20 rounded-full px-6 py-3 mb-6"
            >
              <div className="flex items-center justify-center w-10 h-10 bg-emerald-500 rounded-full animate-pulse">
                <Headphones size={20} className="text-white" />
              </div>
              <span className="text-white font-outfit font-bold text-lg">Support & Assistance 7j/7</span>
              <div className="flex -space-x-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={14} className="text-amber-400 fill-amber-400" />
                ))}
              </div>
            </motion.div>

            <h2 className="font-outfit font-bold text-4xl md:text-5xl lg:text-6xl text-white mb-6">
              Un accompagnement
              <span className="block mt-2 bg-gradient-to-r from-emerald-400 via-teal-300 to-cyan-400 bg-clip-text text-transparent">
                d'exception
              </span>
            </h2>
            <p className="font-manrope text-white/70 text-lg max-w-2xl mx-auto">
              Nos experts certifiés RGE vous accompagnent à chaque étape de votre projet, 
              7 jours sur 7, pour garantir votre réussite.
            </p>
          </motion.div>

          {/* Support Features */}
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            {[
              {
                icon: Phone,
                title: "Assistance téléphonique",
                desc: "Appelez-nous à tout moment pour une réponse immédiate",
                highlight: "Réponse < 2min"
              },
              {
                icon: MessageCircle,
                title: "Chat Expert en direct",
                desc: "Échangez avec nos conseillers via notre chatbot intelligent",
                highlight: "24h/24"
              },
              {
                icon: Clock,
                title: "Suivi personnalisé",
                desc: "Un conseiller dédié suit votre dossier de A à Z",
                highlight: "7j/7"
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="group relative bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 hover:bg-white/10 hover:border-emerald-500/50 transition-all duration-500"
              >
                <div className="absolute -top-3 -right-3">
                  <span className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg">
                    {feature.highlight}
                  </span>
                </div>
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border border-emerald-500/30 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <feature.icon size={28} className="text-emerald-400" />
                </div>
                <h3 className="font-outfit font-bold text-xl text-white mb-3">{feature.title}</h3>
                <p className="font-manrope text-white/60 text-sm">{feature.desc}</p>
              </motion.div>
            ))}
          </div>

          {/* Packs Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h3 className="font-outfit font-bold text-2xl md:text-3xl text-white mb-4">
              Choisissez votre formule d'accompagnement
            </h3>
            <p className="font-manrope text-white/60">
              Des solutions adaptées à tous les projets de rénovation
            </p>
          </motion.div>

          <div className="max-w-2xl mx-auto">
            {/* Pack unique Copilote eco+ */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ type: "spring", damping: 20 }}
              className="relative group"
            >
              {/* Badge */}
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-20">
                <motion.div
                  animate={{ y: [0, -5, 0] }}
                  transition={{ repeat: Infinity, duration: 2 }}
                  className="bg-gradient-to-r from-cyan-500 via-teal-500 to-emerald-500 text-white font-bold px-6 py-2 rounded-full text-sm shadow-lg shadow-teal-500/30 flex items-center gap-2"
                >
                  <Sparkles size={16} />
                  ACCOMPAGNEMENT EXPERT COMPLET
                </motion.div>
              </div>

              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/30 to-emerald-500/30 rounded-[2rem] blur-xl group-hover:blur-2xl transition-all" />
              <div className="relative bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-xl border-2 border-cyan-400/50 rounded-[2rem] p-10 hover:border-cyan-400 transition-all flex flex-col">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <img
                      src="https://customer-assets.emergentagent.com/job_494a1d3d-3b3f-4afe-990a-a7037372e3a6/artifacts/ypve3c19_grok-image-2a6d0623-cb08-456d-9b3d-7d9f13cb557e.png.jpg"
                      alt="Copilote eco+"
                      className="w-14 h-14 rounded-xl object-cover flex-shrink-0"
                    />
                    <div>
                      <h4 className="font-outfit font-bold text-2xl text-white">Pack Copilote eco+</h4>
                      <p className="text-cyan-400 text-sm font-manrope">Un accompagnement d'experts à vos côtés</p>
                    </div>
                  </div>
                  <BadgeCheck size={36} className="text-cyan-400" />
                </div>

                <div className="flex items-baseline gap-2 mb-8">
                  <span className="font-syne text-6xl font-bold text-white">19,90€</span>
                  <span className="text-white/50 text-sm">paiement unique et sécurisé</span>
                </div>

                <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 mb-6">
                  {[
                    "Devis illimités et précis pour vos travaux prioritaires",
                    "Analyses énergétiques complètes + Rapport PDF personnalisé",
                    "Recommandations classées par priorité et budget",
                    "Comparateur d'artisans RGE certifiés + suivi des réponses",
                    "Simulation complète des aides financières mobilisables",
                    "Checklists personnalisées et modèles de mails prêts à l'emploi",
                    "Accompagnement d'experts à vos côtés tout au long du projet",
                    "Support prioritaire 7j/7",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-white font-manrope text-sm list-none">
                      <CheckCircle2 size={16} className="text-cyan-400 flex-shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </div>

                <p className="text-cyan-300/80 italic text-sm mb-8 border-l-2 border-cyan-400/40 pl-4">
                  "Avec un accompagnement d'experts à vos côtés, vos travaux de rénovation deviennent simples, rentables et sans stress."
                </p>

                <Link to="/tarifs">
                  <Button className="w-full bg-gradient-to-r from-cyan-400 via-teal-400 to-emerald-400 text-[#0F172A] rounded-xl py-6 font-outfit font-bold text-lg hover:opacity-90 transition-opacity group shadow-lg shadow-cyan-500/30">
                    Choisir Copilote eco+ — 19,90€
                    <TrendingUp size={20} className="ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>

                <div className="flex items-center justify-center gap-4 mt-4 text-white/40 text-xs">
                  <span className="flex items-center gap-1"><Shield size={12} /> Paiement sécurisé</span>
                  <span>*hors déplacement</span>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Trust Badges */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-wrap justify-center items-center gap-8 mt-16 pt-12 border-t border-white/10"
          >
            {[
              { icon: Shield, text: "Paiement sécurisé" },
              { icon: BadgeCheck, text: "Experts certifiés RGE" },
              { icon: Clock, text: "Support 7j/7" },
              { icon: Star, text: "98% clients satisfaits" }
            ].map((badge, index) => (
              <div key={index} className="flex items-center gap-2 text-white/60">
                <badge.icon size={18} className="text-emerald-400" />
                <span className="font-manrope text-sm">{badge.text}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>
      {/* Pack Mon Accompagnateur Rénove */}
      <section className="relative py-24 overflow-hidden bg-white">
        {/* Décoration fond */}
        <div className="absolute top-0 left-0 w-full h-full pointer-events-none overflow-hidden">
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-blue-50/60 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-72 h-72 bg-slate-50/80 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-4 max-w-6xl relative z-10">
          {/* Étiquette */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 border border-blue-200 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <RotateCcw size={14} />
              499€ intégralement remboursés si éligible*
            </span>
            <h2 className="font-syne text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Votre projet pris en charge{" "}
              <span className="text-emerald-600">à 100%</span>
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto font-manrope">
              Un accompagnateur dédié vous guide de l'analyse de votre logement jusqu'à l'acceptation 
              de vos aides financières — sans rien débourser.
            </p>
          </motion.div>

          {/* Carte centrale */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden"
          >
            {/* Bandeau top */}
            <div className="bg-gradient-to-r from-[#1a2b6d] to-[#2d3f9e] px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <img
                  src="https://customer-assets.emergentagent.com/job_494a1d3d-3b3f-4afe-990a-a7037372e3a6/artifacts/3eesj9ra_IMG_0008.jpeg"
                  alt="Mon Accompagnateur Renov France Renov"
                  className="w-14 h-14 rounded-full object-cover bg-white p-0.5 shadow-md flex-shrink-0"
                />
                <span className="font-outfit font-bold text-white text-lg leading-tight">
                  Pack « Mon accompagnateur rénove »
                </span>
              </div>
              <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-1.5">
                <span className="text-white font-bold text-xl">499€</span>
                <span className="text-white/80 text-sm">remboursés*</span>
              </div>
            </div>

            <div className="p-8 md:p-10">
              <div className="grid md:grid-cols-2 gap-10 items-center">
                {/* Gauche — description + CTA */}
                <div>
                  <p className="text-gray-600 text-base leading-relaxed mb-6 font-manrope">
                    Confiez votre projet de rénovation à un accompagnateur dédié qui vous aide à définir 
                    les bons travaux, à mobiliser les aides financières et à monter votre dossier de A à Z.
                    Le pack est facturé <strong className="text-gray-900">499€</strong>, puis{" "}
                    <strong className="text-emerald-600">intégralement remboursé</strong> par le dispositif 
                    d'aide, si vous êtes éligible.
                  </p>

                  {/* Points forts */}
                  <ul className="space-y-3 mb-8">
                    {[
                      "Un interlocuteur unique de A à Z",
                      "Mobilisation de toutes vos aides disponibles",
                      "Zéro reste à charge si vous êtes éligible",
                      "Suivi jusqu'à l'acceptation du dossier",
                    ].map((item, i) => (
                      <li key={i} className="flex items-center gap-3 text-gray-700 font-medium">
                        <CheckCheck className="text-emerald-500 flex-shrink-0" size={18} />
                        {item}
                      </li>
                    ))}
                  </ul>

                  <Link to="/tarifs">
                    <Button className="bg-[#1a2b6d] hover:bg-[#2d3f9e] text-white px-8 py-4 rounded-2xl font-semibold text-base shadow-lg shadow-blue-200 hover:shadow-xl transition-all group">
                      Découvrir cet accompagnement
                      <ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </Link>
                  <p className="text-gray-400 text-xs mt-3">
                    * Selon les conditions du dispositif d'aide en vigueur.
                  </p>
                </div>

                {/* Droite — 5 étapes */}
                <div className="grid grid-cols-1 gap-3">
                  {[
                    { icon: ClipboardList, title: "Analyse de votre logement", desc: "Échange, collecte d'infos, étude de votre situation" },
                    { icon: TrendingUp,    title: "Recommandations de travaux", desc: "Priorités, gains énergétiques, budget indicatif" },
                    { icon: BadgeEuro,     title: "Vérification des aides",     desc: "État, collectivités, tous les dispositifs" },
                    { icon: FolderOpen,    title: "Montage du dossier complet", desc: "Pièces, formulaires, dépôt du dossier" },
                    { icon: HandCoins,     title: "Suivi administratif",        desc: "Jusqu'à l'acceptation de votre aide" },
                  ].map((step, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.1 + i * 0.07 }}
                      className="flex items-start gap-4 bg-gray-50 rounded-xl p-4 border border-gray-100 hover:border-blue-200 hover:bg-blue-50/40 transition-all"
                    >
                      <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <step.icon className="text-[#1a2b6d]" size={17} />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 text-sm">{step.title}</p>
                        <p className="text-gray-500 text-xs mt-0.5">{step.desc}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

    </div>
  );
}
